import React, { useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import MarketingBackdrop, {
  panelClass,
  softPanelClass,
  ctaSectionClass,
} from "../components/MarketingBackdrop";

const INDUSTRY_CONFIG = {
  security: {
    badge: "Security / Services",
    title: "Book a Neo security demo.",
    intro:
      "Tell us how many sites you operate, how many guards or field staff you manage, and what control gaps you want solved first. We will tailor the demo around your security workflow.",
    productLabel: "Which security mode are you interested in?",
    productOptions: [
      { value: "security_command", label: "Neo Security Command" },
      { value: "security_patrols", label: "Neo Patrols & Checkpoints" },
      { value: "security_incidents", label: "Neo Incident Reporting" },
      { value: "security_suite", label: "Neo Security Full Suite" },
    ],
    cards: [
      "Demo focus can include clock-in verification, patrol proof, checkpoint scans, incident reporting, panic alerts, and supervisor oversight.",
      "We can walk you through how Neo creates proof of presence, escalation clarity, and command-center visibility across sites.",
      "Ideal for guarding companies, mobile response teams, site supervisors, and service businesses that need verified field execution.",
    ],
    messageLabel: "What security challenges do you want Neo to solve first?",
    consentText:
      "I consent to MatrixOS storing my details so you can contact me about this security demo request.",
  },
  retail: {
    badge: "Retail / Hospitality",
    title: "Book a Neo retail demo.",
    intro:
      "Tell us how many sites you operate, how many staff you manage, and which operational gaps you want fixed first. We will shape the demo around your live environment.",
    productLabel: "Which retail mode are you interested in?",
    productOptions: [
      { value: "neo_platform", label: "Neo Platform" },
      { value: "neo_labor", label: "Neo Labor & Staff Control" },
      { value: "neo_reporting", label: "Neo Duties & Reporting" },
      { value: "neo_suite", label: "Neo Retail Full Suite" },
    ],
    cards: [
      "Demo focus can include attendance, duties, shift discipline, incident logging, compliance, and live operations visibility.",
      "A demo is the best route if you want to evaluate fit before talking about rollout or pricing structure.",
      "We will tailor the conversation around your workflow, operating pressure points, and rollout goals.",
    ],
    messageLabel: "What operational chaos are you trying to fix?",
    consentText:
      "I consent to MatrixOS storing my details so you can contact me about this demo request.",
  },
  sport: {
    badge: "Sport Operations",
    title: "Book a Neo sport demo.",
    intro:
      "Tell us about your team size, sites or venues, and the control gaps you want solved first. We will tailor the demo around your sporting environment.",
    productLabel: "Which sport mode are you interested in?",
    productOptions: [
      { value: "sport_readiness", label: "Readiness & Compliance" },
      { value: "sport_staff", label: "Staff & Matchday Coordination" },
      { value: "sport_visibility", label: "Scheduling & Visibility" },
      { value: "sport_suite", label: "Sport Operations Full Suite" },
    ],
    cards: [
      "Demo focus can include readiness checks, staff coordination, compliance workflows, scheduling visibility, and matchday control.",
      "We can shape the walkthrough around clubs, venues, academies, or multi-site sporting operations.",
      "Ideal for sports clubs, venue teams, event support staff, and matchday operations.",
    ],
    messageLabel: "What sport operations challenges do you want Neo to solve first?",
    consentText:
      "I consent to MatrixOS storing my details so you can contact me about this sport demo request.",
  },
};

const inputCls =
  "mt-2 w-full rounded-xl bg-[#0c1a30] border border-white/10 px-4 py-3 outline-none focus:border-cyan-400 text-white";
const utilityLinkCls =
  "border-none bg-transparent text-[0.88rem] font-semibold tracking-[0.03em] text-[rgba(222,246,255,0.84)] cursor-pointer px-0 py-1 transition hover:text-white";
const utilityFooterLinkCls =
  "border-none bg-transparent text-[0.84rem] font-medium tracking-[0.03em] text-[rgba(196,230,242,0.76)] cursor-pointer px-0 py-1 transition hover:text-white";

function Toast({ open, title, message, onClose, tone = "success" }) {
  if (!open) return null;
  const toneClasses =
    tone === "success"
      ? "border-cyan-400/45 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.22),rgba(15,23,42,0.96))] shadow-[0_0_0_1px_rgba(34,211,238,0.12),0_18px_48px_rgba(6,182,212,0.28)]"
      : "border-rose-400/45 bg-[radial-gradient(circle_at_top,rgba(244,63,94,0.18),rgba(15,23,42,0.96))] shadow-[0_0_0_1px_rgba(244,63,94,0.12),0_18px_48px_rgba(244,63,94,0.18)]";

  return (
    <div className="fixed top-24 right-4 z-[9999] max-w-[calc(100vw-2rem)]">
      <div className={`w-[380px] rounded-2xl border p-4 backdrop-blur-xl ${toneClasses}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-100/90">{message}</div>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" type="button">
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm text-slate-200">{label}</label>
      {children}
    </div>
  );
}

export default function BookDemo() {
  const params = new URLSearchParams(window.location.search);
  const industryKey = params.get("industry") || "retail";
  const industry = INDUSTRY_CONFIG[industryKey] || INDUSTRY_CONFIG.retail;
  const defaultProduct = industry.productOptions[0]?.value || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    sites: "",
    staff: "",
    product: defaultProduct,
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    title: "",
    message: "",
    tone: "success",
  });

  const canSubmit = useMemo(
    () =>
      form.name.trim() &&
      form.email.trim() &&
      form.phone.trim() &&
      form.company.trim() &&
      form.sites &&
      form.staff &&
      form.consent,
    [form]
  );

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    try {
      setSubmitting(true);
      await addDoc(collection(db, "public_leads_book_demo"), {
        createdAt: serverTimestamp(),
        source: "website",
        page: "book-demo",
        leadType: "demo_request",
        sendTo: "admin@matrixos.co.za",
        notificationEmail: "admin@matrixos.co.za",
        industry: industryKey,
        industryLabel: industry.badge,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        sites: Number(form.sites) || form.sites,
        staff: Number(form.staff) || form.staff,
        productInterest: form.product,
        productInterestLabel:
          industry.productOptions.find((x) => x.value === form.product)?.label || form.product,
        message: form.message.trim(),
        consentAccepted: true,
        consentAcceptedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        sites: "",
        staff: "",
        product: defaultProduct,
        message: "",
        consent: false,
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
      setToast({
        open: true,
        title: "Demo request sent",
        message: "Thanks — we received it and will contact you shortly.",
        tone: "success",
      });
    } catch (err) {
      console.error(err);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setToast({
        open: true,
        title: "Something went wrong",
        message: "Please try again. If it keeps happening, contact us directly.",
        tone: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingBackdrop>
      <Toast
        open={toast.open}
        title={toast.title}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast({ open: false, title: "", message: "", tone: "success" })}
      />

      <div className="fixed top-[22px] right-[28px] z-20 flex items-center gap-[18px]">
        <button
          type="button"
          className={utilityLinkCls}
          onClick={() => (window.location.href = "/contact")}
        >
          Contact
        </button>
        <button
          type="button"
          className={utilityLinkCls}
          onClick={() => (window.location.href = "/faq")}
        >
          FAQ
        </button>
        <button
          type="button"
          className="rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-bold text-[#dafbff] backdrop-blur-md transition hover:border-cyan-300/40 hover:bg-white/10"
          onClick={() => (window.location.href = "/login")}
        >
          Sign in
        </button>
      </div>

      <div className="fixed left-[28px] bottom-[18px] z-20 text-[0.82rem] font-medium tracking-[0.04em] text-[rgba(196,230,242,0.58)]">
        © 2026 MatrixOS. All rights reserved.
      </div>

      <div className="fixed right-[28px] bottom-[18px] z-20 flex items-center gap-4">
        <button
          type="button"
          className={utilityFooterLinkCls}
          onClick={() => (window.location.href = "/terms")}
        >
          Terms
        </button>
        <button
          type="button"
          className={utilityFooterLinkCls}
          onClick={() => (window.location.href = "/privacy")}
        >
          Privacy
        </button>
        <button
          type="button"
          className={utilityFooterLinkCls}
          onClick={() => (window.location.href = "/billing")}
        >
          Billing
        </button>
      </div>

      <main className="relative max-w-6xl mx-auto px-6 pt-28 pb-20 grid lg:grid-cols-[0.92fr_1.08fr] gap-8">
        <section className={`${panelClass} p-8 md:p-10 self-start`}>
          <div className="mb-3 inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs uppercase tracking-[0.28em] text-cyan-300">
            {industry.badge}
          </div>
          <h1 className="text-4xl font-extrabold leading-tight text-white">{industry.title}</h1>
          <p className="mt-4 text-slate-300 leading-8">{industry.intro}</p>

          <div className="mt-8 space-y-4 text-slate-200/90">
            {industry.cards.map((card) => (
              <div key={card} className={`${softPanelClass} p-4`}>
                {card}
              </div>
            ))}
          </div>
        </section>

        <form onSubmit={handleSubmit} className={`${panelClass} p-8 md:p-10 space-y-5`}>
          <Field label="Name">
            <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </Field>

          <Field label="Email">
            <input
              type="email"
              className={inputCls}
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>

          <Field label="Phone / WhatsApp">
            <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Business / Company name">
              <input className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} />
            </Field>

            <Field label={industry.productLabel}>
              <select className={inputCls} value={form.product} onChange={(e) => set("product", e.target.value)}>
                {industry.productOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="# Sites">
              <input
                inputMode="numeric"
                className={inputCls}
                value={form.sites}
                onChange={(e) => set("sites", e.target.value)}
              />
            </Field>

            <Field label="# Staff">
              <input
                inputMode="numeric"
                className={inputCls}
                value={form.staff}
                onChange={(e) => set("staff", e.target.value)}
              />
            </Field>
          </div>

          <Field label={industry.messageLabel}>
            <textarea
              className={`${inputCls} min-h-[140px]`}
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
            />
          </Field>

          <label className="flex items-start gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => set("consent", e.target.checked)}
              className="mt-1"
            />
            <span>{industry.consentText}</span>
          </label>

          <button
            disabled={!canSubmit || submitting}
            className="w-full rounded-full bg-cyan-400 text-[#071120] font-semibold px-6 py-3 hover:bg-cyan-300 disabled:opacity-50"
          >
            {submitting ? "Sending..." : "Send Demo Request"}
          </button>
        </form>
      </main>

      <section className="relative px-6 pb-28">
        <div className={`max-w-6xl mx-auto ${ctaSectionClass} p-8 md:p-10 text-center`}>
          <div className="mx-auto mb-3 w-fit rounded-full bg-emerald-400/10 px-4 py-1.5 text-[0.72rem] uppercase tracking-[0.26em] text-emerald-200">
            B-BBEE procurement advantage
          </div>
          <p className="max-w-3xl mx-auto text-slate-100 text-lg leading-8">
            If your business spends R10,000 with MatrixOS during rollout, that can count as
            R13,500 toward your procurement score while you improve your operating visibility.
          </p>
        </div>
      </section>
    </MarketingBackdrop>
  );
}
